import { useEffect, useRef, useState } from 'react';
import {
  api, darf, datum, datumKurz, uhr, waehlbar, ZUSTAND_TEXT, zustandText,
  gewerkeVon,
  auswahllisten, type Anmelder, type Auswahllisten,
} from '../api.ts';
import {
  Auswahl, Dialog, Fehlerkasten, Haken, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { Filterklappe } from '../teile/Filterklappe.tsx';

const ZUSTAENDE = ['neu', 'laufend', 'ueberfaellig', 'unterbrochen', 'erledigt'];

/**
 * Welche Zustände dieser Anmelder überhaupt haben kann.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „außerdem hat partner nie „neu" aufträge, denn die sind ja schon im
 *    laufeneden prozess durch den innendienst."
 *
 * ER HAT RECHT, UND ES IST KEINE GESCHMACKSFRAGE. „neu" heisst in
 * `v_auftrag_zustand`: an diesem Auftrag hängt kein Gewerk mit Firma ODER
 * Termin. Ein Sub sieht überhaupt nur Aufträge, an denen SEINE Firma ein
 * Gewerk hat - also kann „neu" für ihn nie zutreffen.
 *
 * Auf seinem Bildschirmfoto steht deshalb „Neu 0", und zwar dauerhaft. **Ein
 * Filter, der immer null trifft, ist kein Filter, sondern ein Knopf, der
 * nichts tut** - und er nimmt auf 375 Pixeln den Platz, den die anderen vier
 * brauchen.
 */
function zustaendeFuer(istSub: boolean): string[] {
  return istSub ? ZUSTAENDE.filter((z) => z !== 'neu') : ZUSTAENDE;
}

/**
 * Die Sortierung steht ueber der Liste, nicht im Programmcode.
 *
 * ORIGINALTEXT MARVIN (23.08.2026)
 *   „nach was sind die sortiert? scheinbar nicht nach dem eingangsdatum oder?"
 *
 * Nein. Sie war fest auf „ueberfaellige zuerst, dann naechster Termin", und
 * das stand nirgends. Eine Ordnung, die man erraten muss, ist keine.
 */
/**
 * DIE KATEGORIEN DES INNENDIENSTES.
 *
 * Wörtlich die des Bildschirms, den Marvin am 07.09.2026 danebengelegt hat -
 * die 3 farbigen zuerst, denn nach denen hat er ausdrücklich gefragt. Was ein
 * Auftrag darin zu suchen hat, RECHNET `v_auftrag_ansteht`; hier stehen nur
 * Aufschrift, Farbe und der Satz dazu.
 *
 * DER SATZ IST NICHT DIE ZIER, SONDERN DIE HÄLFTE DER AUSKUNFT. „Wartegrund
 * fehlt" mit einer 8 daneben sagt nicht, was zu tun ist; der Satz darunter
 * sagt es.
 */
export const ANSTEHT = [
  { wert: 'wartegrund', name: 'Wartegrund fehlt', farbe: 'f-warte',
    satz: 'Einblasen oder Spleißen liegt hinter dem Termin und ist nicht '
        + 'erledigt - und im Auftrag steht kein Grund. Genau diese Auskunft '
        + 'verlangt die Telekom. Eintragen unter „Bearbeiten → Stand".' },
  { wert: 'abrechenbar', name: 'Abrechenbar', farbe: 'f-geld',
    satz: 'Das Aufmaß ist abgegeben und mindestens eine Position ist noch in '
        + 'kein LEB gegangen - und JEDES eigene Gewerk ist fertig gemeldet. '
        + 'Solange ein Partner nicht abgeschlossen hat, steht der Auftrag '
        + 'hier nicht, auch nicht bei einem offenen Einblasstopp (.452): '
        + 'diese Aufträge stehen unter „Einblasstopp", das Teil-LEB geht dort. '
        + 'Der Weg zur Abrechnung ist der Reiter „LEB" im Auftrag.' },
  /* DER CHIP, DEN MARVIN AM 18.09.2026 VERLANGT HAT.
     „bei den aufträgen die erledigt und abgerechnet sind, prüfbar über das
      erzeugte LEB, möchte ich bei der übersichtsseite der aufträge unter
      «erledigt» auch ein chip haben, das «abgerechnet» steht farbig."
     ER STEHT DIREKT HINTER „ABRECHENBAR", denn er ist dessen Gegenstück: das
     eine heisst „es steht noch etwas offen", das andere „es steht nichts mehr
     offen". Ein Auftrag ist nie beides - das misst Migration 259 nach.
     Und er hat seine EIGENE Farbe. Dieselbe wie „Abrechenbar" hiesse, zwei
     Zustände gleich einzufärben, die man gerade auseinanderhalten will. */
  { wert: 'abgerechnet', name: 'Abgerechnet', farbe: 'f-fertig',
    satz: 'Für diesen Auftrag ist mindestens eine LEB-Datei erzeugt, und '
        + 'keine Aufmaßposition steht mehr offen. Es ist dieselbe Rechnung, '
        + 'mit der die Plattform einen Auftrag auf „erledigt" setzt - der '
        + 'Nachweis ist das erzeugte LEB.' },
  { wert: 'aufmass', name: 'Aufmaß prüfen', farbe: 'f-pruefen',
    satz: 'Der Partner hat sein Aufmaß eingereicht und wartet auf das '
        + 'Gegenhalten. Abgegeben ist nicht geprüft.' },
  { wert: 'doku', name: 'Doku prüfen', farbe: '',
    satz: 'Die Montage-Dokumentation ist eingereicht und noch nicht '
        + 'abgeschlossen.' },
  { wert: 'heute', name: 'Termin heute', farbe: '',
    satz: 'An diesen BAUSTELLEN wird heute gearbeitet - der erste Tag zählt '
        + 'wie jeder Folgetag eines mehrtägigen Tiefbaus. Gezählt werden '
        + 'Aufträge und nicht Termine: wird an einem Haus heute eingeblasen '
        + 'und gespleißt, ist das 1 Baustelle und 2 Termine.' },
  /* DIE GEGENPROBE ZUM ERSTEN KNOPF.
     Marvin, 08.09.2026: „wo finde ich die anderen offenen punkte als
     menüpunkt aus dem alten system?" Nachgezählt an seiner Ausfuhr: die
     Vorgängerlösung kannte 7 Klassen, und 6 davon stehen in der oberen
     Reihe. Es fehlte kein Menüpunkt, sondern genau diese eine Kategorie -
     „Wartegrund fehlt" nimmt sie ausdrücklich heraus, und danach suchen
     konnte man nirgends. */
  { wert: 'wartet', name: 'Wartet', farbe: 'f-warte',
    satz: 'Hier steht ein Wartegrund im Auftrag - jemand hat eingetragen, '
        + 'warum es hängt. Die Gegenprobe zu „Wartegrund fehlt". Zu lesen '
        + 'und zu ändern unter „Bearbeiten → Stand".' },
  /* DER EINBLASSTOP ALS FILTER UND NICHT ALS REITER (.230).
     Marvin: „warum stehen hier die einblasstops. das wäre ein filterpunkt
     unter "Aufträgen"". Er stand als Reiter unter „Aufmaß und Abrechnung". */
  { wert: 'einblasstopp', name: 'Einblasstopp', farbe: 'f-warte',
    satz: 'Beim Einblasen ist ein Stop gemeldet und noch nicht zurückgenommen. '
        + 'Meter, Seite und Grund stehen in der Akte unter „Montage"; ein '
        + 'Teilaufmaß kann damit schon abgegeben werden.' },
];

/* DIE GEWERKAUSWAHL STEHT EINMAL DA.
   Sie war im Auswahlfeld eingebaut, und der Knopf darüber muss denselben Text
   nennen. Zweimal geschrieben wären es 2 Texte, die auseinanderlaufen, sobald
   einer davon geändert wird. */
export const GEWERKWAHL = [
  { wert: 'TB', text: 'nur mit Tiefbautermin' },
  { wert: 'EB', text: 'nur mit Einblastermin' },
  { wert: 'SP', text: 'nur mit Spleißtermin' },
];

const SORTIERUNGEN = [
  { wert: 'termin', text: 'Termin: überfällige zuerst', kurz: 'was brennt' },
  { wert: 'eingang', text: 'Eingang: neueste zuerst', kurz: 'was neu ist' },
  { wert: 'eingang_alt', text: 'Eingang: älteste zuerst', kurz: 'was am längsten liegt' },
  { wert: 'adresse', text: 'Adresse: Ort, Straße, Nummer', kurz: 'nach Ort' },
  { wert: 'sm', text: 'SM-Nummer', kurz: 'nach SM-Nummer' },
];

/**
 * Die Arbeitsliste.
 *
 * ORIGINALTEXT MARVIN (23.08.2026, mit Bildschirmfoto)
 *   „da stimmt die anordnung nicht, die SM nummer felt komplett. viel zu viel
 *    platz bei der baustellenadresse. „nächster" keine ahung was da ist, kann
 *    da raus."
 *   „wichtig sind die einträge AI auf dem ersten blick."
 *
 * A bis I sind die ersten Spalten seines Masters: Adresse_Key, SM_Nummer,
 * KLS-ID, BA/FA ID, Straße und Hausnummer, PLZ, Ort, Navigation, Gemeinde.
 * Der Ident- und Adressblock, und in dieser Reihenfolge steht er jetzt hier.
 * Die SM-Nummer fehlte ganz, die KLS stand als letzte Spalte rechts außen.
 *
 * Die Spalte „Nächster" ist weg. Sie zeigte ein Datum ohne zu sagen, welches
 * Gewerk gemeint ist; das steht jetzt an den Gewerkemarken selbst, wo die
 * Frage aufkommt.
 */
/**
 * WAS EINGESTELLT WAR, IST NACH DEM ZURÜCKGEHEN NOCH EINGESTELLT.
 *
 * ORIGINALTEXT MARVIN (28.08.2026)
 *   „wenn ich eine fiterung wähle, zb „neu" und öffne dann einen auftrag,
 *    springe dann wieder zurück auf „liste", dann läd er aber wieder alle
 *    anstatt meine letzte markierte filterung beizubehalten. das will ich
 *    ändern."
 *
 * Die Ursache steckt nicht in der Liste, sondern im Wechsel: wer einen Auftrag
 * öffnet, hängt diese Komponente AUS (`App.tsx` zeigt dann die Akte). Beim
 * Zurückgehen wird sie neu gebaut, und `useState('')` fängt wieder bei null
 * an. Für React ist das richtig; für jemanden, der einunddreissig überfällige
 * Aufträge abarbeitet, ist es einunddreissigmal dieselbe Handbewegung.
 *
 * ES STEHT AUSSERHALB DER KOMPONENTE und überlebt damit das Aushängen. Nicht
 * im Speicher des Browsers: der Filter ist eine Absicht für JETZT, keine
 * Einstellung. Wer morgen die Liste aufmacht, will sie ganz sehen - und ein
 * gemerkter Filter, der einen Neustart überlebt, ist genau die Falle, bei der
 * jemand eine leere Liste für „nichts zu tun" hält.
 *
 * Gemerkt wird ALLES, was die Ansicht ausmacht: auch Suche und Sortierung.
 * Wer „Koblenz" gesucht hat und einen Auftrag angesehen hat, sucht danach
 * weiter in Koblenz.
 */
/* UND ER GEHÖRT DEM ANGEMELDETEN, NICHT DEM BROWSERFENSTER.
 *
 * ORIGINALTEXT MARVIN (31.08.2026, mit einem Bildschirmfoto: „0 von 149",
 * „Nichts gefunden", und KEIN Filterknopf hervorgehoben)
 *   „wenn ich zurück auf die liste springe zeigt er keine aufträge direkt an.
 *    er soll «alle anzeigen»"
 *
 * ZWEI DINGE LIEFEN ZUSAMMEN, und keines davon war das Merken selbst.
 *
 *   * DER GEMERKTE ZUSTAND ÜBERLEBTE „LOGIN ALS". Er steht ausserhalb der
 *     Komponente, also überlebt er auch den Wechsel des Benutzers - und ein
 *     Innendienstfilter in der Ansicht einer Partnerfirma ist ein Filter aus
 *     einer anderen Welt.
 *   * DEN CHIP DAZU GAB ES NICHT MEHR. „neu" ist für einen Partner seit dem
 *     31.08.2026 kein Filter mehr („der partner hat nie neue aufträge"), und
 *     damit stand der Filter ZWAR IN DER ABFRAGE, aber auf keinem Knopf.
 *
 * Zusammen ergab das den schlimmsten aller Zustände: eine leere Liste, die
 * nicht sagt, warum sie leer ist. **Ein Filter, den man nicht sieht, ist
 * keiner - er ist ein Fehler.**
 *
 * Der Filter wird deshalb VERWORFEN, wenn er in dieser Ansicht nicht wählbar
 * ist, und er trägt den Benutzer, für den er gilt. Marvins Anweisung vom
 * 28.08.2026 („behalte meine letzte markierte filterung bei") bleibt damit
 * unangetastet: gemerkt wird weiter, nur nichts Unsichtbares.
 */
/* ============================================================================
 * UND SEIT .88 HÄNGT SIE AM ZUGANG, NICHT NUR AM FENSTER.
 * ============================================================================
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „grundsätzlich ist es wichtig, wenn ich als benutzer filtereinstellungen
 *    oder sortierungen vornehme, dass die in meinem profil gespeichert werden
 *    und wnen ich mich wieder einlogge auch so stehen."
 *
 * Der Merker unten überlebt den Bildschirmwechsel und sonst nichts: weder das
 * Neuladen noch das Anmelden am zweiten Gerät. Wer morgens das Tablet nimmt,
 * hat eine andere Liste als am Abend zuvor am Schreibtisch.
 *
 * ER BLEIBT TROTZDEM STEHEN, und zwar als schnelle Fassung: der Server
 * antwortet nicht in derselben Millisekunde, in der der Bildschirm gezeichnet
 * wird. Gespeichert wird gebremst (eine Sekunde nach der letzten Änderung) -
 * sonst schreibt jeder Buchstabe in der Suche eine Zeile in die Datenbank.
 *
 * WAS GEMERKT WIRD, IST NIE EIN RECHT. Was jemand sehen DARF, entscheiden
 * weiterhin seine Gruppen und seine Firma; ein gemerkter Filter kann nur
 * weniger zeigen, nie mehr.
 */
/* ============================================================================
 * UND SEIT .172 HÄNGT SIE AM GERÄT UND NICHT MEHR AM ZUGANG.
 * ============================================================================
 * ORIGINALTEXT MARVIN (19.09.2026)
 *   „wenn ich hier am PC eine suche bei den aufträgen mache und gehe dann auf
 *    die mobile version, dann ist dort auch der suchbegriff drin und wird
 *    gefiltert. also auch wenn ich mobile mit dem PC und Handy auf dem
 *    gleichen benutzer arbeite, so sind das unterschiedliche sessions. das
 *    musst du trennen."
 *
 * ER HAT RECHT, UND DER SATZ VON OBEN WAR SCHON FALSCH: „weder das Neuladen
 * noch das Anmelden am zweiten Gerät" - genau das tat der Merker seit .88
 * doch, weil er am PROFIL hing. Ein Profil gilt für den Menschen, und der
 * sitzt an zwei Geräten gleichzeitig.
 *
 * **EIN SUCHBEGRIFF IST KEINE EIGENSCHAFT DES MENSCHEN, SONDERN DER
 * SITZUNG, IN DER ER GERADE SUCHT.** Wer am Schreibtisch nach einer Strasse
 * sucht und dann drausssen das Handy aufmacht, will die volle Liste - und
 * nicht dieselben 2 Zeilen, ohne zu wissen, warum.
 *
 * Gemerkt wird ab jetzt im GERÄT (localStorage), unter einem Schlüssel, der
 * den Benutzer trägt: zwei Menschen an einem Rechner bekommen ihre eigene
 * Ansicht, ein Mensch an zwei Geräten zwei. Marvins Anweisung vom 03.09.2026
 * („dass die in meinem profil gespeichert werden und wenn ich mich wieder
 * einlogge auch so stehen") bleibt damit erfüllt, nur eben je Gerät.
 *
 * ES HÄLT NICHTS AUF. In einem Fenster ohne Speicher (privates Fenster,
 * gesperrte Seitendaten) wirft der Zugriff, und dann gilt einfach die leere
 * Ansicht. Ein Bildschirm, der an einer gemerkten Bequemlichkeit scheitert,
 * wäre die teuerste Art Ordnungsliebe.
 * ==========================================================================*/
const ANSICHT_SCHLUESSEL = (wer: string) => `mds.ansicht.auftraege.${wer}`;

function ansichtLesen(wer: string): any {
  try {
    const roh = window.localStorage.getItem(ANSICHT_SCHLUESSEL(wer));
    return roh ? JSON.parse(roh) : null;
  } catch { return null; }
}

function ansichtSchreiben(wer: string, was: unknown): void {
  try {
    window.localStorage.setItem(ANSICHT_SCHLUESSEL(wer), JSON.stringify(was));
  } catch { /* ohne Speicher geht die Liste trotzdem */ }
}

/* DER MERKER IM FENSTER TRÄGT JETZT AUCH DIE ZEILEN.
   ==========================================================================
   ORIGINALTEXT MARVIN (19.09.2026)
     „dann braucht das system sehr lange, wenn ich vorher gefiltert habe und
      bin dann in einem auftrag drin, gehe dann wieder in die gefilterte
      übersicht, dass er sehr lange braucht um die gefilterten ergebnisse
      anzuzeigen. er läd das ganze dann wieder neu anstatt es aus dem cache zu
      öffnen."

   Der Merker hielt bisher die 5 Filterwerte und warf die Antwort weg. Wer aus
   einem Auftrag zurückkam, sah deshalb eine leere Fläche mit „wird geladen",
   obwohl sich in den 20 Sekunden dazwischen nichts geändert hat.

   **EINE LISTE, DIE MAN GERADE ERST GESEHEN HAT, MUSS NICHT ERST WIEDER
   ERSCHEINEN.** Ab jetzt steht sie sofort wieder da, und die frische Antwort
   zieht im Hintergrund nach - ohne Ladeanzeige, weil nichts fehlt. Die Zeilen
   gelten nur für DIESELBEN Filter und nur eine begrenzte Zeit; eine Liste von
   gestern wäre keine Bequemlichkeit, sondern eine Falschauskunft. */
const CACHE_MS = 5 * 60 * 1000;

let zuletzt: {
  wer: string; suche: string; zustand: string; ansteht: string;
  gewerk: string; sortierung: string; daten: any; wann: number;
} = { wer: '', suche: '', zustand: '', ansteht: '', gewerk: '',
      sortierung: 'termin', daten: null, wann: 0 };

/* DIE LETZTEN LISTEN, JE FILTER (.230).
   Marvin: „bei der sucheingabe bei den aufträgen, filterrücksetzung usw. das
   ganze langsamer geworden ist. er cache glaube ich nicht richtig."
   Er hatte recht: gemerkt wurde nur die EINE letzte Liste. Wer „Läuft"
   anklickt und dann den Filter zurücksetzt, wartete auf die Liste, die eben
   noch dastand. Jetzt stehen die letzten 12 Listen im Speicher: sie sind
   sofort da und ziehen danach still nach. Je Zugang, damit nach einem
   Wechsel nichts Fremdes aufblitzt. */
const vorrat = new Map<string, any>();
const VORRAT_HOECHSTENS = 12;
function vorratLegen(k: string, daten: any) {
  vorrat.delete(k); vorrat.set(k, daten);
  while (vorrat.size > VORRAT_HOECHSTENS) vorrat.delete(vorrat.keys().next().value!);
}

/* Der Schlüssel, unter dem ein Listenstand gilt. 5 Werte, die dieselben sein
   müssen - steht einer anders, ist es eine andere Liste. */
const filterSchluessel = (f: { suche: string; zustand: string; ansteht: string;
                               gewerk: string; sortierung: string }) =>
  [f.suche, f.zustand, f.ansteht, f.gewerk, f.sortierung].join('\u0000');

export default function Arbeitsliste({
  ich, oeffne,
}: {
  ich: Anmelder;
  oeffne: (id: string, reiter?: string) => void;
}) {
  /* WAS AUS EINER ANDEREN ANSICHT STAMMT, GILT HIER NICHT. Geprüft wird
     beides: derselbe Benutzer, und ein Zustand, den es auf diesem Bildschirm
     als Knopf gibt. */
  const meins = zuletzt.wer === ich.benutzerId;
  const erlaubt = zustaendeFuer(!!ich.istSub);
  /* DIE GEMERKTE ANSICHT AUS DEM PROFIL ist der Anfang, der Merker im Fenster
     hat Vorrang: wer gerade einen Filter gesetzt und den Bildschirm gewechselt
     hat, will ihn wiederfinden und nicht den vom Vortag. */
  /* SIE KOMMT AUS DEM GERÄT UND NICHT MEHR AUS DEM PROFIL. Der Bezeichner
     heisst weiter so, weil er an 6 Stellen darunter steht; was sich geändert
     hat, ist die QUELLE - siehe den Block über dieser Funktion. */
  const ausProfil = ansichtLesen(ich.benutzerId);
  const start = meins
    ? { ...zuletzt,
        zustand: erlaubt.includes(zuletzt.zustand) ? zuletzt.zustand : '' }
    : ausProfil
      ? { suche: String(ausProfil.suche ?? ''),
          ansteht: String(ausProfil.ansteht ?? ''),
          zustand: erlaubt.includes(String(ausProfil.zustand ?? ''))
            ? String(ausProfil.zustand) : '',
          gewerk: String(ausProfil.gewerk ?? ''),
          sortierung: String(ausProfil.sortierung ?? 'termin') }
      : { suche: '', zustand: '', ansteht: '', gewerk: '', sortierung: 'termin' };

  const [suche, setSuche] = useState(start.suche);
  const [zustand, setZustand] = useState(start.zustand);
  /* WAS ANSTEHT - die 2. Reihe. Sie wird wie die anderen gemerkt: wer aus
     „Abrechenbar" einen Auftrag öffnet, will danach in „Abrechenbar" zurück. */
  const [ansteht, setAnsteht] = useState(
    ANSTEHT.some((a) => a.wert === (start as any).ansteht)
      ? String((start as any).ansteht) : '');
  const [gewerk, setGewerk] = useState(start.gewerk);
  const [sortierung, setSortierung] = useState(start.sortierung);
  /* DIE FILTERKNÖPFE SIND AUF DEM TELEFON ZUGEKLAPPT.
   *
   * ORIGINALTEXT MARVIN (22.09.2026)
   *   „2/3 der Seite belegen irgendwelche Menüs und Auswahlmöglichkeiten."
   *
   * ZU, NICHT AUF: wer die Liste aufmacht, will die Liste. Der Knopf darüber
   * sagt, was gewählt ist, und öffnet sie in 1 Tipp. Auf dem Schreibtisch ist
   * dieser Zustand ohne Wirkung - dort steht `.filterfach` immer offen, und
   * der Knopf ist gar nicht erst da. */
  /* DER LETZTE LISTENSTAND, WENN ER NOCH GILT.
     „Gilt" heisst: derselbe Mensch, dieselben 5 Filter, und nicht älter als
     CACHE_MS. Alle 3 Bedingungen zusammen - **eine Liste, die zu anderen
     Filtern gehört, ist schlimmer als gar keine**, weil sie aussieht wie eine
     Antwort. */
  const ausCache = (meins
    && zuletzt.daten
    && Date.now() - zuletzt.wann < CACHE_MS
    && filterSchluessel(zuletzt) === filterSchluessel(start as any))
    ? zuletzt.daten : null;
  const [daten, setDaten] = useState<any>(ausCache);
  const [listen, setListen] = useState<Auswahllisten | null>(null);
  /* WAS ANGEHAKT IST, UND WELCHES FENSTER OFFEN.
     ------------------------------------------------------------------------
     ORIGINALTEXT MARVIN (03.09.2026)
       „außerdem brauche ich als administrator die möglichkeit aufträge manuell
        anzulegen und zu löschen ... auch das sammellöschen brauche ich. ich
        will den ganze testbestand dann darüber auch einmal bereinigen wegen
        den dopplungen."

     DIE HAKEN STEHEN NUR DA, WO SIE ETWAS BEWIRKEN: wer nicht löschen darf,
     bekommt die Spalte gar nicht erst. Ein Haken, der zu einem grauen Knopf
     führt, ist eine Einladung ins Leere. */
  const [markiert, setMarkiert] = useState<Set<string>>(new Set());
  const [fenster, setFenster] = useState<null | 'anlegen' | 'loeschen'>(null);
  const darfLoeschen = darf(ich, 'auftrag.loeschen');
  const darfAnlegen = darf(ich, 'auftrag.anlegen');
  /* UND DIE LADEANZEIGE STEHT NUR DA, WENN WIRKLICH NICHTS DA IST. Ein
     „wird geladen" über einer vollständigen Liste ist keine Auskunft, sondern
     Unruhe. */
  const [laedt, setLaedt] = useState(!ausCache);
  /* WIE VIELE ZEILEN SCHON DA SIND, UND OB ES NOCH MEHR GIBT.
   *
   * ORIGINALTEXT MARVIN (02.09.2026)
   *   „die auftragsliste ist bei 50 einträgen einfach zu ende. er soll zu
   *    jedem zeitpunkt bei den aufträgen immer alle aufträge anzeigen in
   *    jeder kategorie, keine seiten zum blättern oder sowas. einfach
   *    scrollbar nach unten."
   *
   * DER DECKEL LAG IM SERVER UND HATTE KEINE ANZEIGE. `proSeite` steht seit
   * jeher auf 50, und die Oberfläche hat immer nur die ERSTE Seite geholt -
   * es gab keinen Knopf für die zweite und keinen Satz, der sagte, dass es
   * eine gibt. **Ein Deckel, der nichts sagt, wenn er greift, ist eine
   * Falle**, und diese hier las sich wie „mehr Aufträge gibt es nicht".
   * Geladen wird jetzt in Blöcken und AUTOMATISCH nachgeladen, sobald man
   * unten ankommt; die Zeile über der Liste sagt weiter „X von Y".
   */
  const [mehrLaeuft, setMehrLaeuft] = useState(false);
  const fuss = useRef<HTMLDivElement>(null);

  /** Wie viele Zeilen je Block geholt werden. Der Server deckelt bei 200. */
  const BLOCK = 100;

  /* WIE VIELE ZEILEN DIESE AUSWAHL HAT - UND NICHT, WIE VIELE ES GIBT.
   *
   * ORIGINALTEXT MARVIN (07.09.2026, mit einem Bildschirmfoto: „3 von 7",
   * darunter „3 von 7 geladen, wird geladen")
   *   „er ist die ganze zeit unten am laden ist, es flackert, da er im
   *    sekundentakt alles neu läd wegen der suche."
   *
   * DAS FLACKERN KAM NICHT VON DER SUCHE, SONDERN VON DIESER ZAHL. `anzahl`
   * ist mit Absicht eine Zählung JE ZUSTAND und mit Absicht OHNE den
   * Zustandsfilter - sonst stünden die anderen Knöpfe auf 0, sobald man einen
   * anklickt (Marvin, 25.08.2026). Das Nachladen hat sie aufsummiert und
   * damit gegen den GANZEN Bestand verglichen: bei „Läuft" standen 3 Zeilen
   * da und 7 in der Summe, also holte der Beobachter die nächste Seite, bekam
   * nichts, und der Fuss war weiter im Blick - eine Anfrage nach der anderen.
   *
   * **Die drei Riegel von .103 waren richtig; die Zahl, gegen die sie
   * vergleichen, war die falsche.**
   *
   * UND SIE WAR ES AM 08.09.2026 NOCH EINMAL, EINE REIHE WEITER. Die Fassung
   * von .134 rechnete aus `anzahl` - bei gesetztem Zustand dessen Zahl, sonst
   * die Summe - und kannte die zweite Reihe nicht, die es seit .131 gibt. Auf
   * Marvins Bild stand unter zwölf Zeilen mit gewähltem „Wartet 12":
   * **„12 von 416 geladen, wird geladen"**.
   *
   * **DIE ZAHL DER LISTE LÄSST SICH AUS DEN KNOPFZAHLEN NICHT RECHNEN, und
   * seit dem 08.09.2026 grundsätzlich nicht mehr.** Beide Reihen zählen
   * ausdrücklich OHNE die Auswahl der anderen, damit kein Knopf dem anderen
   * seine Zahl wegnimmt; die Liste daneben ist die SCHNITTMENGE. Eine
   * Rechnung, die daraus eine Zahl macht, ist bei zwei gesetzten Filtern
   * geraten - und einmal zu gross heisst: eine Schleife.
   *
   * Sie kommt deshalb vom SERVER (`gefunden`), aus derselben Bedingung, die
   * auch die Zeilen holt. Eine zweite Rechnung daneben liefe beim nächsten
   * Filter wieder auseinander. */
  const zeilenDerAuswahl = (d: any) => Number(d?.gefunden ?? 0);

  const frageBauen = () => {
    const p = new URLSearchParams();
    if (suche) p.set('suche', suche);
    if (zustand) p.set('zustand', zustand);
    if (ansteht) p.set('ansteht', ansteht);
    if (gewerk) p.set('gewerk', gewerk);
    if (sortierung) p.set('sortierung', sortierung);
    p.set('proSeite', String(BLOCK));
    return p;
  };

  useEffect(() => { auswahllisten().then(setListen).catch(() => {}); }, []);

  useEffect(() => {
    /* HIER GEMERKT UND NICHT IN JEDEM `onClick`. Die Ansicht hängt an genau
       diesen vier Werten - dieselbe Abhängigkeitsliste, die den Abruf
       auslöst. Wer es an die Knöpfe schriebe, vergässe den fünften. */
    const jetztFilter = { suche, zustand, ansteht, gewerk, sortierung };
    /* DIE ZEILEN BLEIBEN STEHEN, SOLANGE DIE FILTER DIESELBEN SIND.
       Wer einen Filter UMSTELLT, bekommt keinen alten Stand mehr gezeigt -
       die Zeilen gehören dann zu einer anderen Frage. */
    const passt = filterSchluessel(zuletzt) === filterSchluessel(jetztFilter)
               && zuletzt.wer === ich.benutzerId;
    zuletzt = { wer: ich.benutzerId, ...jetztFilter,
                daten: passt ? zuletzt.daten : null,
                wann: passt ? zuletzt.wann : 0 };

    /* AUS DEM VORRAT SOFORT, WENN DIESE LISTE SCHON EINMAL DA WAR (.230). */
    const vk = ich.benutzerId + '\u0001' + filterSchluessel(jetztFilter);
    if (!passt && vorrat.has(vk)) {
      zuletzt = { ...zuletzt, daten: vorrat.get(vk), wann: Date.now() };
      setDaten(vorrat.get(vk));
    }

    const p = frageBauen();
    /* EINE ÜBERHOLTE ANTWORT ZÄHLT NICHT (.230). Kam die Antwort auf „Kob"
       nach der auf „Koblenz", stand die falsche Liste da - und es sah aus,
       als hätte der Klick nicht gewirkt. */
    let ueberholt = false;
    const abbruch = setTimeout(() => {
      /* NUR MELDEN, WENN NICHTS DASTEHT. Steht die Liste schon, zieht die
         frische Antwort still nach - das ist der ganze Unterschied zwischen
         „lädt" und „ist aktuell". */
      if (!zuletzt.daten) setLaedt(true);
      api.auftraege(p).then((d) => {
        if (ueberholt) return;
        vorratLegen(vk, d);
        setDaten(d);
      }).finally(() => { if (!ueberholt) setLaedt(false); });
    }, suche ? 220 : 0);
    return () => { ueberholt = true; clearTimeout(abbruch); };
  }, [suche, zustand, ansteht, gewerk, sortierung, ich.benutzerId]);

  /* DER MERKER ZIEHT NACH, EGAL WOHER DIE ZEILEN KAMEN.
     An EINER Stelle und nicht in jedem `then`: die Zeilen kommen aus dem
     ersten Abruf UND aus dem Nachladen beim Scrollen, und wer nur den ersten
     merkt, wirft beim Zurückkommen die 3 nachgeladenen Blöcke weg.
     GEPRÜFT WIRD DER FILTER TROTZDEM: eine späte Antwort darf die Zeilen der
     vorigen Frage nicht in den Merker der neuen legen. */
  useEffect(() => {
    if (!daten) return;
    const jetztFilter = { suche, zustand, ansteht, gewerk, sortierung };
    if (zuletzt.wer === ich.benutzerId
        && filterSchluessel(zuletzt) === filterSchluessel(jetztFilter)) {
      zuletzt = { ...zuletzt, daten, wann: Date.now() };
    }
  }, [daten, suche, zustand, ansteht, gewerk, sortierung, ich.benutzerId]);

  /* UND SIE GEHT AN DEN ZUGANG.
   *
   * GEBREMST, mit einer eigenen Wartezeit: die Abfrage oben soll bei jedem
   * Buchstaben nachziehen, das Profil nicht. Eine Sekunde nach der letzten
   * Änderung reicht - wer den Bildschirm vorher schliesst, hat den Merker im
   * Fenster, und beim nächsten Anmelden fehlt höchstens der letzte Handgriff.
   *
   * UND SIE HÄLT NICHTS AUF. Geht das Speichern schief, arbeitet die Liste
   * weiter; ein Bildschirm, der an einer gemerkten Einstellung scheitert,
   * wäre die teuerste Art Ordnungsliebe. */
  useEffect(() => {
    const t = setTimeout(() => {
      ansichtSchreiben(ich.benutzerId,
        { suche, zustand, ansteht, gewerk, sortierung });
    }, 1000);
    return () => clearTimeout(t);
  }, [suche, zustand, ansteht, gewerk, sortierung, ich.benutzerId]);

  /* DEN NÄCHSTEN BLOCK HOLEN.
   *
   * Er HÄNGT AN, er ersetzt nicht: eine Liste, die beim Nachladen von vorn
   * beginnt, springt unter dem Finger weg. Und er läuft nur einmal
   * gleichzeitig - ohne den Riegel feuert der Beobachter beim Scrollen
   * dreimal und holt denselben Block dreifach. */
  async function mehrHolen() {
    if (mehrLaeuft || laedt || !daten) return;
    /* UND NUR, SOLANGE ES ETWAS ZU HOLEN GIBT.
     *
     * OHNE DIESE ZEILE LÄUFT DIE LISTE ENDLOS. Der Beobachter feuert, sobald
     * der Fuss im Blick ist - und der ist es bei einer kurzen Liste IMMER.
     * Ohne Deckel holte jeder Lauf die nächste Seite, bekam eine leere Antwort,
     * merkte sich die höhere Seitenzahl und fing von vorn an: eine Anfrage
     * nach der anderen, bis jemand die Seite schliesst. Gefunden hat es
     * Prüfstand 041, der auf „keine Netzlast mehr" wartet und nie
     * zurückkam - kein Übersetzer und kein Blick auf die Seite zeigt das,
     * denn zu SEHEN ist nichts. */
    const schon = daten.zeilen?.length ?? 0;
    if (schon >= zeilenDerAuswahl(daten)) return;
    const naechste = (Number(daten.seite) || 1) + 1;
    setMehrLaeuft(true);
    try {
      const p = frageBauen();
      p.set('seite', String(naechste));
      const d = await api.auftraege(p);
      setDaten((alt: any) => (alt ? {
        ...d,
        /* DIE ZÄHLUNG KOMMT AUS DER NEUEN ANTWORT, die Zeilen kommen dazu.
           Beides aus derselben Antwort zu nehmen wäre falsch herum: `anzahl`
           gilt für die ganze Liste, `zeilen` nur für diesen Block.
           UND EINE LEERE ANTWORT ZÄHLT NICHT WEITER: bliebe die höhere
           Seitenzahl stehen, versuchte es der nächste Lauf mit der
           übernächsten und käme nie zur Ruhe. */
        seite: d.zeilen.length ? d.seite : alt.seite,
        zeilen: [...alt.zeilen, ...d.zeilen],
      } : d));
    } catch {
      /* Nichts zu tun: der Knopf darunter bleibt stehen, und wer ihn drückt,
         versucht es noch einmal. Ein roter Kasten mitten im Scrollen wäre
         lauter als der Fehler. */
    } finally { setMehrLaeuft(false); }
  }

  /* AUTOMATISCH, SOBALD MAN UNTEN ANKOMMT. „Keine Seiten zum Blättern" heisst
     nicht „ein Knopf statt einer Seitenzahl": es heisst, dass man nichts tun
     muss. Der Knopf bleibt trotzdem darunter stehen - ein Beobachter, den ein
     Browser nicht kennt oder den ein Zoom nie auslöst, dürfte die Liste nicht
     abschneiden. */
  const vollstaendig = !!daten
    && (daten.zeilen?.length ?? 0) >= zeilenDerAuswahl(daten);

  useEffect(() => {
    const ziel = fuss.current;
    /* STEHT SCHON ALLES DA, WIRD NICHT MEHR BEOBACHTET. Ein Beobachter auf
       einer vollständigen Liste ist kein Nachladen, sondern eine Schleife. */
    if (!ziel || vollstaendig
        || typeof IntersectionObserver === 'undefined') return;
    const b = new IntersectionObserver((eintraege) => {
      if (eintraege.some((e) => e.isIntersecting)) mehrHolen();
    }, { rootMargin: '400px' });
    b.observe(ziel);
    return () => b.disconnect();
  });

  /** Die Liste neu holen - nach dem Anlegen und nach dem Löschen. */
  async function neuLaden() {
    setLaedt(true);
    try { setDaten(await api.auftraege(frageBauen())); }
    finally { setLaedt(false); }
  }
  /* UND NACH JEDEM ANDEREN SCHREIBVORGANG AUCH. Der Bildschirm hängt aus,
     sobald eine Akte aufgeht (`App.tsx` zeichnet entweder die Liste ODER die
     Akte), also zieht er nur nach, solange ihn wirklich jemand ansieht. */
  useFrisch(neuLaden);

  const zahl = (z: string) =>
    daten?.anzahl.find((a: any) => a.zustand === z)?.n ?? 0;

  /* ZWEI ZAHLEN, UND SIE BEANTWORTEN VERSCHIEDENE FRAGEN. `gesamt` steht auf
     dem Knopf „Alle" und sagt, wie viele Treffer es OHNE Zustandsauswahl gibt;
     `imBlick` sagt, wie viele WIRKLICH in dieser Liste stehen - mit jedem
     Filter, den jemand gesetzt hat. Nur die gehört über die Liste und in das
     Nachladen. Sie waren dieselbe Zahl, und deshalb stand über einer Liste mit
     3 Zeilen „3 von 7" und über einer mit 12 Zeilen „12 von 416". */
  const gesamt = (daten?.anzahl ?? [])
    .reduce((n: number, x: any) => n + x.n, 0);
  const imBlick = zeilenDerAuswahl(daten);

  /* WAS AUF DEM ZUGEKLAPPTEN KNOPF STEHT. Dieselben Wörter wie auf den
     Knöpfen dahinter - ein Knopf, der die Auswahl mit anderen Wörtern
     benennt als die Stelle, an der man sie trifft, ist ein zweiter Text zum
     Lernen. Die Suche steht nicht dabei: sie hat ihr eigenes Feld, das
     sichtbar bleibt. */
  const filterWorte = [
    zustand ? ZUSTAND_TEXT[zustand] : '',
    ansteht ? (ANSTEHT.find((a) => a.wert === ansteht)?.name ?? '') : '',
    gewerk ? (GEWERKWAHL.find((g) => g.wert === gewerk)?.text ?? '') : '',
  ].filter(Boolean);

  return (
    <>
      {/* DAS SUCHFELD STEHT NICHT MEHR IM KOPF.
          Marvin, 25.08.2026: „Dies komplette suchfeld oben rechts nach unten
          ziehen unter diese auswahl/zuordnungsfelder."
          Es gehoert zur Liste und nicht zum Bildschirmtitel: Zustand, Gewerk
          und Suche schraenken DIESELBE Liste ein, und drei Dinge, die
          dasselbe tun, gehoeren nebeneinander. Im Kopf sass es ausserdem an
          derselben Stelle, an der die Glocke sitzt. */}
      <div className="kopf">
        <h1>Aufträge</h1>
        <span className="wachsen" />
        {darfAnlegen && (
          <button className="knopf" onClick={() => setFenster('anlegen')}>
            Auftrag anlegen
          </button>
        )}
        {/* DER LÖSCHKNOPF STEHT NUR DA, WENN ETWAS MARKIERT IST, und er sagt
            WIE VIELE. „Löschen" allein neben einer Liste mit vierhundert
            Zeilen ist die Sorte Knopf, die man einmal aus Versehen drückt. */}
        {darfLoeschen && markiert.size > 0 && (
          <button className="knopf gefahr" onClick={() => setFenster('loeschen')}>
            {markiert.size === 1 ? '1 Auftrag löschen'
                                 : `${markiert.size} Aufträge löschen`}
          </button>
        )}
        <span className="weg">{ich.istSub ? ich.firma : 'Innendienst'}</span>
      </div>

      <div className="inhalt">
        {/* DER KNOPF, DER DIE FILTER AUFMACHT - UND NUR AUF DEM TELEFON.
            ------------------------------------------------------------------
            ORIGINALTEXT MARVIN (22.09.2026)
              „Oder die mobile Version muss allgemein angepasst werden. 2/3 der
               Seite belegen irgendwelche Menüs und Auswahlmöglichkeiten."

            ER SAGT DIE AUSWAHL UND NICHT NUR „FILTER". Steht nichts drin,
            heisst er „Alle Aufträge" - das ist eine Auskunft und kein
            Platzhalter. Steht etwas drin, stehen die Wörter da, die auf den
            Knöpfen dahinter stehen: **wer 8 von 430 Zeilen sieht, muss lesen
            können, warum.**

            `display: none` AUF DEM SCHREIBTISCH, in `stil.css`. Dort war der
            Platz nie das Problem, und dort sind die Zahlen an den Knöpfen eine
            Übersicht, die man im Vorbeigehen liest. */}
        {/* .617: die Klappe ist ein Bauteil (teile/Filterklappe.tsx), dasselbe wie in NE4. */}
        <Filterklappe worte={filterWorte}>

        {/* DIE FILTERLEISTE BLEIBT BEIM ROLLEN OBEN STEHEN. Sie beantwortet
            dieselbe Frage wie eine Reiterleiste - „was von dieser Liste sehe
            ich gerade" -, und bei vierhundert Zeilen ist sie nach dem ersten
            Rollen sonst weg. */}
        <div className="reihe reiterreihe">
          {/* DIE AUSWAHL IST GEFUELLT, NICHT NUR UMRANDET.
              Marvin, 25.08.2026: „außerdem das ausgewählte oben farblich
              hervorheben."
              Ein farbiger Rand neben sechs grauen Raendern ist ein
              Unterschied, den man sucht. Ein gefuellter Knopf ist einer, den
              man sieht. */}
          <button
            className={'knopf filter' + (!zustand ? ' gewaehlt' : '')}
            aria-pressed={!zustand}
            onClick={() => setZustand('')}
          >
            Alle
            <span className="filterzahl">{gesamt}</span>
          </button>
          {zustaendeFuer(!!ich.istSub).map((z) => (
            <button
              key={z}
              className={'knopf filter' + (zustand === z ? ' gewaehlt' : '')}
              aria-pressed={zustand === z}
              onClick={() => setZustand(zustand === z ? '' : z)}
            >
              {ZUSTAND_TEXT[z]}
              {/* DIE ZAHLEN BLEIBEN STEHEN, AUCH WENN GEFILTERT IST.
                  Marvin, 25.08.2026: „wenn ich die einzelnen punkte ,neu,
                  läuft...' durchklicke, dann verschwinden bei den anderen die
                  zahlen. das will ich nicht."
                  Die Zaehlung kommt seit dem 25.08.2026 aus einer Abfrage
                  OHNE den Zustandsfilter (`routen/auftrag.ts`). Vorher zaehlte
                  sie mit ihm, und dann standen alle anderen Knoepfe auf 0 -
                  die Zahlen sahen aus wie eine Auskunft ueber den Bestand und
                  waren eine ueber die eigene Auswahl. */}
              <span className="filterzahl">{zahl(z)}</span>
            </button>
          ))}
          <div className="wachsen" />
          <select value={gewerk} onChange={(e) => setGewerk(e.target.value)}>
            <option value="">Alle Gewerke</option>
            {GEWERKWAHL.map((g) => (
              <option key={g.wert} value={g.wert}>{g.text}</option>
            ))}
          </select>
        </div>

        {/* WAS ANSTEHT - DIE ZWEITE REIHE.
         *
         * ORIGINALTEXT MARVIN (07.09.2026, mit dem Bildschirm der
         * Vorgängerlösung daneben)
         *   „dann fehlen uns diese kategorien, wo wir die aufträge einfacher
         *    finden können um damit zu arbeiten. grade was die drei kategorien
         *    mti den farben betrifft so hilft dem innendienst diese sortierung
         *    um den überblck zu behalten."
         *
         * SIE IST EINE EIGENE REIHE UND KEINE ERWEITERUNG DER ERSTEN. Die
         * Reihe darüber beantwortet „in welchem Zustand ist dieser Auftrag" -
         * jeder hat genau einen, deshalb schliessen sich die Knöpfe aus. Diese
         * beantwortet „was muss ich heute anfassen", und da kann ein Auftrag
         * in mehreren stehen. Zusammengelegt nähmen die Knöpfe einander weg,
         * was sie zeigen sollen.
         *
         * DIE FARBEN SIND DIE DER SACHE UND KEINE ZIER: `z-ueberfaellig` für
         * das, was die Telekom von uns will, `z-erledigt` für Geld, das
         * abgerufen werden kann. Sie kommen aus `stil.css` wie überall -
         * dieselben drei, die Marvin auf seinem Bild markiert hat.
         *
         * EIN KNOPF OHNE TREFFER STEHT GAR NICHT ERST DA. Ein Filter, der
         * immer null trifft, ist kein Filter, sondern ein Knopf, der nichts
         * tut - dieselbe Lehre wie beim Filter „neu" für den Partner und beim
         * Ordnerfilter im Dateireiter. Der gerade GEWÄHLTE bleibt stehen, auch
         * wenn seine Zahl auf null fällt: sonst verschwindet der Knopf unter
         * dem Finger, der ihn gerade gedrückt hat. */}
        {/* UND SIE GEHÖRT DEM INNENDIENST.
            ------------------------------------------------------------------
            ORIGINALTEXT MARVIN (18.09.2026)
              „die zweiten buttons zur weiteren filterung sind nur für uns
               innendienst"

            ER HAT RECHT, UND ES WAR MEHR ALS EINE FRAGE DES PLATZES. Von den
            7 Kategorien beantworten 6 Fragen des Innendienstes: was die
            Telekom von UNS will, was WIR abrechnen können, was WIR schon
            abgerechnet haben, was WIR prüfen müssen. **„Abgerechnet" sagt
            einer Partnerfirma, dass wir ihre Leistung weiterberechnet
            haben** - eine Auskunft über unser Verhältnis zur Telekom, die auf
            ihrem Bildschirm nichts zu suchen hat.

            DIE ZAHL FÄLLT MIT. Sie stand in derselben Reihe und hätte sonst
            dasselbe verraten wie der Knopf. Der Partner behält die obere
            Reihe (neu, läuft, überfällig, unterbrochen, erledigt): das ist
            der Zustand SEINER Aufträge. */}
        {!ich.istSub
         && daten?.ansteht && ANSTEHT.some((a) => (daten.ansteht[a.wert] ?? 0) > 0) && (
          <div className="reihe reiterreihe ansteht">
            <span className="klein leise anstehtkopf">Was ansteht</span>
            {ANSTEHT.filter((a) => (daten.ansteht[a.wert] ?? 0) > 0
                                || ansteht === a.wert).map((a) => (
              <button
                key={a.wert}
                className={'knopf filter' + (ansteht === a.wert ? ' gewaehlt' : '')
                         + (a.farbe ? ' ' + a.farbe : '')}
                aria-pressed={ansteht === a.wert}
                title={a.satz}
                onClick={() => setAnsteht(ansteht === a.wert ? '' : a.wert)}
              >
                {a.name}
                <span className="filterzahl">{daten.ansteht[a.wert] ?? 0}</span>
              </button>
            ))}
            {ansteht && (
              <button className="knopf klein" onClick={() => setAnsteht('')}>
                zurücksetzen
              </button>
            )}
          </div>
        )}

        {/* UND WAS DIE GEWÄHLTE KATEGORIE BEDEUTET, STEHT DA. Ein Knopf mit 2
            Wörtern beantwortet nicht, warum diese 8 Aufträge hier stehen -
            und genau das ist die Frage, mit der jemand die Liste aufmacht. */}
        {ansteht && (
          <p className="klein leise" style={{ margin: '0 0 10px' }}>
            {ANSTEHT.find((a) => a.wert === ansteht)?.satz}
          </p>
        )}

        </Filterklappe>

        {/* DAS SUCHFELD BLEIBT DRAUSSEN. Es ist kein Filter, den man einmal am
            Tag setzt, sondern der Weg zu EINEM Auftrag - und danach sucht
            jemand am häufigsten. Hinter einem Knopf wäre es das Gegenteil von
            dem, was Marvin am 07.09.2026 verlangt hat: „wenn kamil einen
            auftrag sucht ... die sucht immer über ,alle' laufen." */}
        <div className="reihe" style={{ marginBottom: 10 }}>
          <input
            type="search" autoComplete="off"
            /* DIE KENNUNG STEHT SEIT DEM 31.08.2026 MIT DABEI, und sie steht
               vorn: seit Migration 063 ist sie die Identität eines Auftrags.
               Ein Platzhalter, der eine Suchmöglichkeit verschweigt, ist so
               gut wie keine - niemand tippt versuchsweise eine Nummer ein,
               die laut Beschriftung nicht gesucht wird. */
            placeholder="BA/FA-ID, Bauauftrag-ID, Adresse, Ort, KLS, SM-Nummer, Kunde"
            value={suche}
            /* EINE SUCHE LÄUFT ÜBER ALLE.
             *
             * ORIGINALTEXT MARVIN (07.09.2026)
             *   „wenn kamil einen auftrag sucht, dann springt er immer in die
             *    kategorie ,erledigt'. ich schlage vor, generell soll wenn man
             *    was sucht die sucht immer über ,alle' laufen."
             *
             * Er ist nirgendwohin gesprungen: der Zustandsfilter STAND schon
             * da (gemerkt am Zugang, seit dem 03.09.2026) und galt für die
             * Suche mit. Wer eine SM-Nummer eintippt, sucht diesen einen
             * Auftrag und nicht „diesen Auftrag, falls er zufällig erledigt
             * ist" - und die leere Liste danach liest sich wie „den gibt es
             * nicht". Dieselbe Lehre wie bei der BA/FA-ID am 31.08.2026, nur
             * eine Etage weiter: dort fehlte die SPALTE, hier steht ein
             * Filter davor.
             *
             * Der Filter fällt beim ERSTEN Zeichen und nicht bei jedem: wer
             * danach bewusst auf „Erledigt" klickt, bleibt dort. */
            onChange={(e) => {
              const w = e.target.value;
              if (w && !suche) { setZustand(''); setAnsteht(''); }
              setSuche(w);
            }}
            style={{ flex: '1 1 340px', maxWidth: 520 }}
          />
          {suche && (
            <button className="knopf klein" onClick={() => setSuche('')}>
              Suche zurücksetzen
            </button>
          )}
        </div>

        {/* Die Ordnung steht ueber der Liste und ist waehlbar. */}
        <div className="listenkopf">
          <span>
            {laedt ? 'wird geladen' : `${daten?.zeilen.length ?? 0} von ${imBlick}`}
          </span>
          <span className="wachsen" />
          {/* BESCHRIFTUNG UND AUSWAHL SIND EIN STÜCK.
              Marvin, 22.09.2026: „dass Sortierung nach und das dropdown doch
              sicher noch in eine Reihe passen."
              Als 2 getrennte Kinder einer umbrechenden Flexzeile brach die
              Zeile GENAU zwischen ihnen um. In einer eigenen Hülle brechen sie
              nur noch gemeinsam um. Der Rest steht bei `.sortierwahl` in
              `stil.css`. */}
          <span className="sortierwahl">
            <label htmlFor="sortierung">Sortiert nach</label>
            <select id="sortierung" value={sortierung}
                    onChange={(e) => setSortierung(e.target.value)}>
              {SORTIERUNGEN.map((s) => (
                <option key={s.wert} value={s.wert}>{s.text}</option>
              ))}
            </select>
          </span>
        </div>

        <div className="karte">
          {/* DIE TABELLE ROLLT IN IHRER EIGENEN KARTE.

              ORIGINALTEXT MARVIN (31.08.2026)
                „die mobile ansicht macht noch darstellungsfehler im
                 hochformat. bitte anpassen."

              AUF SEINEM BILDSCHIRMFOTO IST DIE BAUSTELLE ABGESCHNITTEN:
              „Schwarzer…", „56070 Kobler…", und die beiden Spalten dahinter
              fehlen ganz. Die Regel für das Telefon setzt `overflow-x: auto`
              auf `.karte > .leib` - und diese Tabelle steht DIREKT in der
              Karte, ohne `.leib` dazwischen. Also gab es keinen rollenden
              Vorfahren, und die Karte hat den Überstand abgeschnitten.

              PRÜFSTAND 014 WAR DABEI GRÜN, zum dritten Mal aus demselben
              Grund: er misst, ob die SEITE seitlich rollt. Sie tut es nicht -
              abgeschnitten rollt nichts. Gefunden hat es ein
              Bildschirmfoto. */}
          <div className="rollt">
          <table className="liste auftragsliste">
            <thead>
              <tr>
                {/* DIE BREITEN MUESSEN ZUSAMMEN IN DIE KARTE PASSEN.
                    Sie summierten sich mit der frei laufenden Auftragsinfo auf
                    mehr als das Doppelte der verfuegbaren Breite; die
                    Gewerkespalte stand dann ausserhalb des Bildschirms. Die
                    Auftragsinfo bekommt jetzt den Rest und bricht um. */}
                {darfLoeschen && (
                  <th style={{ width: 34 }}>
                    {/* ALLE ANHAKEN, WAS GERADE DASTEHT - und nicht, was die
                        Datenbank hat. Ein Haken, der 400 Zeilen markiert, von
                        denen 50 zu sehen sind, ist eine Falle. */}
                    <input type="checkbox" aria-label="Alle sichtbaren markieren"
                           checked={!!daten?.zeilen?.length
                             && daten.zeilen.every((z: any) => markiert.has(z.id))}
                           onChange={(e) => {
                             const neu = new Set(markiert);
                             for (const z of daten?.zeilen ?? []) {
                               if (e.target.checked) neu.add(z.id);
                               else neu.delete(z.id);
                             }
                             setMarkiert(neu);
                           }} />
                  </th>
                )}
                <th style={{ width: 96 }}>Zustand</th>
                <th style={{ width: 100 }}>SM-Nummer</th>
                <th style={{ width: 88 }}>KLS</th>
                <th style={{ width: 190 }}>Baustelle</th>
                <th style={{ width: 140 }}>Auftragstyp</th>
                <th style={{ width: 160 }}>Kunde</th>
                <th>Auftragsinfo</th>
                <th style={{ width: 118 }}>Gewerke</th>
              </tr>
            </thead>
            <tbody>
              {daten?.zeilen.map((a: any) => {
                const soll = a.gewerke_soll ?? gewerkeVon(listen, a.auftragstyp_mds);
                return (
                  <tr key={a.id} onClick={() => oeffne(a.id)}
                      className={markiert.has(a.id) ? 'markiert' : undefined}>
                    {darfLoeschen && (
                      /* DER KLICK AUF DEN HAKEN ÖFFNET NICHT DIE AKTE. Ohne
                         das Anhalten des Klicks springt der Bildschirm beim
                         Markieren in den Auftrag - und man findet die Liste
                         danach nicht wieder. */
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={markiert.has(a.id)}
                               aria-label={`${a.strasse ?? ''} ${a.hausnummer ?? ''} markieren`}
                               onChange={(e) => {
                                 const neu = new Set(markiert);
                                 if (e.target.checked) neu.add(a.id);
                                 else neu.delete(a.id);
                                 setMarkiert(neu);
                               }} />
                      </td>
                    )}
                    <td className="nichtumbrechen">
                      {/* DIE MARKE SAGT, WAS SIE MEINT.
                          ------------------------------------------------
                          ORIGINALTEXT MARVIN (22.09.2026)
                            „Warum stehen hier Aufträge auf «überfällig»
                             wenn der Termin erst in in der Zukunft liegt?
                             Da stimmt doch was nicht."

                          Hier stand nur „Überfällig". Der Text kommt aus
                          `zustandText` in `api.ts`, weil die Akte dieselbe
                          Marke zeigt - 2 Abschriften wären 2 Texte, sobald
                          einer geändert wird. */}
                      <span className={'marke-zustand z-' + a.zustand}
                            title={a.zustand === 'ueberfaellig'
                                     && a.ueberfaellig_nachfolger_geplant
                                   ? 'Für ein späteres Gewerk steht schon '
                                     + 'ein Termin. Dann ist die Arbeit '
                                     + 'meist getan und nur der Haken '
                                     + 'fehlt.'
                                   : undefined}>
                        {zustandText(a)}
                      </span>
                      {/* DER CHIP „ABGERECHNET", UNTER „ERLEDIGT".
                          ------------------------------------------------
                          ORIGINALTEXT MARVIN (18.09.2026)
                            „bei den aufträgen die erledigt und abgerechnet
                             sind, prüfbar über das erzeugte LEB, möchte ich
                             bei der übersichtsseite der aufträge unter
                             «erledigt» auch ein chip haben, das
                             «abgerechnet» steht farbig."
                          und danach, auf das Bild der laufenden .166:
                            „der chip ist nicht da. behebe den fehler!"

                          ER HATTE RECHT, UND ZWAR ZUR HÄLFTE: Migration 259
                          hat die Kategorie gebaut, der Knopf „Abgerechnet 21"
                          steht oben und zählt richtig - aber die ZEILE trug
                          die Angabe nie. **Eine Kategorie ist eine Auswahl,
                          ein Chip ist eine Auskunft**, und wer die Liste
                          ohne gesetzten Filter ansieht, und das ist der
                          Regelfall, sah von der Abrechnung nichts.

                          ER STEHT UNTER UND NICHT NEBEN DER ZUSTANDSMARKE.
                          Die Spalte ist 1 Marke breit; eine zweite daneben
                          drückt auf 375 Bildpunkten die Anschrift aus der
                          Zeile. Untereinander trägt sie dieselbe Stelle,
                          an der schon „wartet" und „Mängelbeseitigung"
                          stehen.

                          GERECHNET WIRD ER NICHT HIER. Der Wert kommt aus
                          `v_auftrag_ansteht`, derselben Sicht, aus der die
                          Zahl über der Liste stammt. Eine Bedingung, die die
                          Oberfläche noch einmal selbst formuliert, weicht
                          beim ersten Nachtrag ab - und dann widerspricht der
                          Chip der Zahl über ihm. */}
                      {a.abgerechnet && (
                        <div className="marke-zustand z-abgerechnet"
                             style={{ marginTop: 4 }}
                             title="Es liegt ein LEB vor, und es steht keine Position mehr offen.">
                          Abgerechnet
                        </div>
                      )}
                      {/* „VORBEREITET" IN GRÜN, NUR BEI NEU (.437). Marvin: „sodass
                          man in der Übersicht sieht unter neu, welcher ist
                          vorbereitet ... im Zusammenspiel der verschiedenen
                          Mitarbeiter." */}
                      {a.vorbereitet && (
                        <div className="marke-zustand z-erledigt" style={{ marginTop: 4 }}
                             title="Die Vorbereitung ist vollständig gespeichert; der Auftrag kann terminiert werden.">
                          Vorbereitet
                        </div>
                      )}
                      {/* .496 hatte hier „WFMT neu (n)" als Chip. Raus seit .502, Marvin:
                          „WFMT neu soll nicht mit einem Chip gekennzeichnet werden. Es
                          reicht, wenn man im Auftrag drin ist und das dann sieht." */}
                      {a.wartet && <div className="klein leise">wartet</div>}
                      {/* Zwei Aufträge auf einer Anschrift haben hier einen
                          Grund, und er steht dabei. */}
                      {a.maengelbeseitigung && (
                        <div className="klein leise">Mängelbeseitigung</div>
                      )}
                    </td>
                    <td className="kennung">
                      {a.sm_nummer ?? <span className="leise">fehlt</span>}
                    </td>
                    <td className="kennung leise">
                      {a.kls_id ?? <span className="leise">fehlt</span>}
                    </td>
                    <td>
                      <b>
                        {a.strasse} {a.hausnummer}
                        {a.hausnummer_zusatz ?? ''}
                      </b>
                      <div className="leise klein">{a.plz} {a.ort_text}</div>
                      {/* EINE ANSCHRIFT, DIE MEHRFACH DASTEHT, SAGT WARUM.
                          Marvin, 14.09.2026 (Bild 9): „zeigt 3-mal dieselbe
                          adresse". Nachgemessen sind es 3 ECHTE Aufträge -
                          die KLS-ID kennzeichnet das Haus, und ein Auftrag
                          zur Mängelbeseitigung liegt mit Absicht auf der
                          Anschrift eines früheren. Ohne diesen Satz liest
                          sich das wie ein Fehler in der Liste, und wo es
                          wirklich Doppelgänger sind, sucht sie niemand.
                          Die Zahl kommt vom Server und wird hier nicht
                          gerechnet: die Oberfläche hat nur die Zeilen DIESER
                          Seite, und über die Anschrift sagt eine Seite
                          nichts. */}
                      {a.an_dieser_anschrift > 1 && (
                        <div className="klein leise">
                          {a.an_dieser_anschrift} Aufträge an dieser Anschrift
                        </div>
                      )}
                    </td>
                    {/* Die Gemeinde stand hier und steht jetzt nicht mehr:
                        sie ist aus der PLZ ablesbar und hat den Platz belegt,
                        den die Auftragsinfo braucht. In der Akte steht sie
                        weiterhin. */}
                    {/* Ausgeschrieben, nicht abgekuerzt. Marvin, 23.08.2026:
                        „Typ ist der Auftragstyp musst du nicht abpürzen.
                         schreibe das aus, wie es war." */}
                    {/* BULK ODER IBT UNTER DEM AUFTRAGSTYP.
                        ORIGINALTEXT MARVIN (07.09.2026)
                          „infor mit IBT oder BULK muss bei den Aurägen in der
                           übersicht stehen."

                        ZWEI ANGABEN, EINE SPALTE - UND SIE STEHEN NICHT
                        GLEICH DA. Oben `auftragstyp_mds`: daraus folgt,
                        welche Gewerke unsere sind. Darunter, als Marke, die
                        Rohspalte der Telekom. Sie in dieselbe Zeile zu
                        schreiben wäre die naheliegende Sparsamkeit und die
                        falsche: dann liest sich „Hausanschluss komplett,
                        BULK" wie EIN Wert, und beim nächsten Filter sucht
                        jemand danach.

                        EINE EIGENE SPALTE WÄRE SIE NICHT WERT: sie trägt
                        zwei Wörter, und die Liste hat auf 375 Bildpunkten
                        ohnehin zu viele.

                        WER DEN BLOCK „VERWALTUNG" NICHT HAT, BEKOMMT DAS FELD
                        GAR NICHT ERST - dann steht hier nichts, und das ist
                        richtig so. */}
                    <td className="klein">
                      {a.auftragstyp_mds ?? <span className="leise">fehlt</span>}
                      {a.auftragstyp_tk !== undefined
                        && (a.auftragstyp_tk ?? []).length > 0 && (
                        <div style={{ marginTop: 3 }}>
                          {(a.auftragstyp_tk as string[]).map((t) => (
                            <span key={t} className="gewerk"
                                  title="Auftrag Typ der Telekom, aus dem Abruf">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="klein">
                      {a.kunde_name ?? <span className="leise">nicht erfasst</span>}
                      <Rufnummern a={a} />
                    </td>
                    <td className="klein">
                      <Auftragsinfo a={a} />
                    </td>
                    <td className="nichtumbrechen">
                      <Gewerke gewerke={a.gewerke ?? []} soll={soll} />
                    </td>
                  </tr>
                );
              })}
              {/* EINE LEERE LISTE SAGT, WAS SIE LEER MACHT - UND HAT EINEN
                  WEG HERAUS. „Für diesen Filter gibt es keine Aufträge" nennt
                  den Filter nicht, und wenn er nicht einmal auf einem Knopf
                  steht, sucht man den Fehler im System statt in der Auswahl.
                  Genau das ist am 31.08.2026 passiert. */}
              {!laedt && daten?.zeilen.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 28, textAlign: 'center' }}>
                    <b>Nichts gefunden.</b>
                    <div className="leise klein" style={{ marginTop: 4 }}>
                      {[suche && `Suche „${suche}"`,
                        zustand && `Zustand „${ZUSTAND_TEXT[zustand] ?? zustand}"`,
                        gewerk && `Gewerk „${gewerk}"`]
                        .filter(Boolean).length
                        ? 'Eingeschränkt auf: '
                          + [suche && `Suche „${suche}"`,
                             zustand && `Zustand „${ZUSTAND_TEXT[zustand] ?? zustand}"`,
                             gewerk && `Gewerk „${gewerk}"`].filter(Boolean).join(', ')
                          + '.'
                        : `Es gibt ${gesamt === 0 ? 'noch keine' : 'keine'} `
                          + 'Aufträge für Sie.'}
                    </div>
                    {(suche || zustand || ansteht || gewerk) && (
                      <button className="knopf" style={{ marginTop: 10 }}
                              onClick={() => {
                                setSuche(''); setZustand(''); setGewerk('');
                              }}>
                        Alle anzeigen
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>

          {/* NICHTS VERSCHWINDET STILL - AUCH KEIN AUSGEBLENDETER ORT.
              -------------------------------------------------------------
              ORIGINALTEXT MARVIN (08.09.2026)
                „das soll imer und überall immer abgeglichen werden ... überall
                 sollech die falschen orte raus sein."

              Der Ortsfilter stand seit dem 01.09.2026 in der Auswertung, der
              Kapazität und dem Terminplaner - in DIESER Liste stand er nicht.
              Er steht jetzt darin, und die Zahl daneben ist die andere Hälfte:
              **ein Filter, der Zeilen wegnimmt und dabei schweigt, ist von
              einem Datenverlust nicht zu unterscheiden.** Wer eine Baustelle
              sucht, die er gestern noch gesehen hat, sucht sie sonst im
              Papier.

              JE ORT UND MIT ZAHL, wie im Import: „14 Zeilen ausgeblendet" ist
              keine Auskunft, „Kappel (14)" ist eine - dann sieht man sofort,
              ob der richtige Ort gemeint war. */}
          {!!daten?.ausgeblendet?.length && (
            <div className="klein leise" style={{ marginTop: 10 }}>
              Ausgeblendet, weil wir dort nicht arbeiten:{' '}
              {daten.ausgeblendet
                .map((o: any) => `${o.ort} (${o.n})`).join(', ')}.
              {' '}Zu ändern unter Konfiguration → Betrieb → „Orte, in denen
              wir nicht arbeiten".
            </div>
          )}

          {/* DAS ENDE DER LISTE SAGT, OB ES EIN ENDE IST.
              ORIGINALTEXT MARVIN (02.09.2026)
                „die auftragsliste ist bei 50 einträgen einfach zu ende ...
                 einfach scrollbar nach unten."
              Hier stand NICHTS - die Liste hörte auf, und niemand konnte
              unterscheiden, ob das alle waren. Jetzt lädt sie beim
              Herunterscrollen von selbst nach; der Knopf steht als zweiter
              Weg daneben, und wenn wirklich alle da sind, steht es da. */}
          {daten && daten.zeilen.length > 0 && (
            <div ref={fuss} className="listenfuss">
              {daten.zeilen.length < imBlick ? (
                <>
                  <span className="klein leise">
                    {daten.zeilen.length} von {imBlick} geladen
                  </span>
                  <button className="knopf klein" disabled={mehrLaeuft}
                          onClick={mehrHolen}>
                    {mehrLaeuft ? 'wird geladen' : 'Weitere laden'}
                  </button>
                </>
              ) : (
                <span className="klein leise">
                  {imBlick === 1 ? 'Der eine Auftrag steht da.'
                                : `Alle ${imBlick} Aufträge stehen da.`}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {fenster === 'anlegen' && (
        <AuftragAnlegen zu={() => setFenster(null)}
                        fertig={async (id: string) => {
                          setFenster(null);
                          await neuLaden();
                          /* WER EINEN AUFTRAG ANLEGT, WILL IHN DANACH VOR
                             SICH HABEN. Sonst sucht er ihn in einer Liste mit
                             vierhundert Zeilen. */
                          oeffne(id);
                        }} />
      )}

      {fenster === 'loeschen' && (
        <AuftraegeLoeschen ids={[...markiert]} zu={() => setFenster(null)}
                           fertig={async () => {
                             setFenster(null);
                             setMarkiert(new Set());
                             await neuLaden();
                           }} />
      )}
    </>
  );
}


/**
 * Einen Auftrag von Hand anlegen.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „außerdem brauche ich als administrator die möglichkeit aufträge manuell
 *    anzulegen und zu löschen."
 *
 * DIE ANSCHRIFT IST PFLICHT UND SONST FAST NICHTS. Ein Auftrag ohne Strasse
 * ist keine Baustelle; alles andere trägt der Import nach oder ein Mensch
 * später. Zwanzig Pflichtfelder wären an dem Tag im Weg, an dem jemand schnell
 * einen Mängelauftrag braucht - und genau dafür ist dieser Weg gedacht.
 */
function AuftragAnlegen({ zu, fertig }: any) {
  const [w, setW] = useState<any>({
    strasse: '', hausnummer: '', hausnummer_zusatz: '', plz: '', ort: '',
    sm_nummer: '', kls_id: '', bafa_id: '', bauauftrag_id: '',
    auftragstyp_mds: '', prio: '',
  });
  const [maengel, setMaengel] = useState(false);
  const [trotzdem, setTrotzdem] = useState(false);
  const [listen, setListen] = useState<Auswahllisten | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  useEffect(() => { auswahllisten().then(setListen).catch(() => {}); }, []);
  const setze = (n: string) => (v: string) => setW({ ...w, [n]: v });

  return (
    <Dialog titel="Auftrag anlegen" laeuft={laeuft} zu={zu} breite={640}>
      <Fehlerkasten lage={fehler} />
      <div className="feldreihe">
        <Text name="strasse" titel="Straße" pflicht breite={260}
              wert={w.strasse} setze={setze('strasse')} fehler={fehler} />
        <Text name="hausnummer" titel="Nr." pflicht breite={90}
              wert={w.hausnummer} setze={setze('hausnummer')} fehler={fehler} />
        <Text name="hausnummer_zusatz" titel="Zusatz" breite={90}
              wert={w.hausnummer_zusatz} setze={setze('hausnummer_zusatz')}
              fehler={fehler} />
      </div>
      <div className="feldreihe">
        <Text name="plz" titel="PLZ" pflicht breite={110}
              wert={w.plz} setze={setze('plz')} fehler={fehler}
              hinweis="An ihr hängen Bundesland, Feiertage und Luftbild." />
        <Text name="ort" titel="Ort" pflicht breite={220}
              wert={w.ort} setze={setze('ort')} fehler={fehler} />
      </div>
      <div className="feldreihe">
        <Text name="sm_nummer" titel="SM-Nummer" breite={150}
              wert={w.sm_nummer} setze={setze('sm_nummer')} fehler={fehler} />
        <Text name="kls_id" titel="KLS-ID" breite={130}
              wert={w.kls_id} setze={setze('kls_id')} fehler={fehler} />
        <Text name="bafa_id" titel="BA/FA-ID" breite={170}
              wert={w.bafa_id} setze={setze('bafa_id')} fehler={fehler}
              hinweis="Sie ist die Kennung, an der der Import wiedererkennt." />
        <Text name="bauauftrag_id" titel="Bauauftrag-ID" breite={150}
              wert={w.bauauftrag_id} setze={setze('bauauftrag_id')} fehler={fehler} />
      </div>
      <div className="feldreihe">
        <Auswahl name="auftragstyp_mds" titel="Auftragstyp" breite={280}
                 wert={w.auftragstyp_mds} setze={setze('auftragstyp_mds')}
                 fehler={fehler} leerText="leer heißt: alle 3 Gewerke"
                 optionen={listen?.auftragstyp ?? []} />
        <Auswahl name="prio" titel="Priorität" breite={150}
                 wert={w.prio} setze={setze('prio')} fehler={fehler}
                 leerText="keine" optionen={listen?.prio ?? []} />
      </div>
      {/* ZWEI AUFTRÄGE AUF EINER ANSCHRIFT SIND DER AUSNAHMEFALL UND KEIN
          FEHLER. Marvin, 02.09.2026: „wir haben aufträge zur mängelbeseitigung
          bekommen, diese haben die gleiche adresse, aber eine andere BAFA und
          eine andere SM-Nummer." */}
      <Haken name="maengelbeseitigung" titel="Auftrag zur Mängelbeseitigung"
             wert={maengel} setze={setMaengel}
             hinweis="Er liegt mit Absicht auf derselben Anschrift wie sein
                      Vorgänger und wird nicht als Dublette gezählt." />
      <Haken name="trotzdem" titel="Trotzdem anlegen, auch wenn dort schon einer steht"
             wert={trotzdem} setze={setTrotzdem}
             hinweis="Ohne diesen Haken weist der Server einen zweiten Auftrag
                      auf derselben Anschrift ab - das ist der Riegel gegen die
                      Dublette." />
      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt"
                disabled={laeuft || !w.strasse || !w.hausnummer || !w.plz || !w.ort}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    const r = await api.auftragAnlegen({
                      ...w, maengelbeseitigung: maengel, trotzdem });
                    await fertig(r.id);
                  } catch (f) { setFehler(ausFehler(f)); }
                  finally { setLaeuft(false); }
                }}>
          {laeuft ? 'Einen Moment' : 'Anlegen'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


/**
 * Aufträge wirklich löschen.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „wenn ich sie lösche, sollen auch die daten aus der der datenbank
 *    verschwinden. auch das sammellöschen brauche ich. ich will den ganze
 *    testbestand dann darüber auch einmal bereinigen wegen den dopplungen."
 *
 * **DIE PROBE STEHT VOR DEM EINGRIFF.** Sie sagt je Auftrag, was daran hängt:
 * Termine, Aufmaße, Dateien, Behördenpapiere. Genau daran erkennt man den
 * Griff in die falsche Liste - und zwar VORHER, denn hinterher gibt es nichts
 * mehr nachzusehen.
 */
function AuftraegeLoeschen({ ids, zu, fertig }: any) {
  const [probe, setProbe] = useState<any>(null);
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    api.auftraegeLoeschenProbe(ids).then(setProbe)
      .catch((f) => setFehler(ausFehler(f)));
  }, []);

  return (
    <Dialog titel={ids.length === 1 ? 'Auftrag löschen'
                                    : `${ids.length} Aufträge löschen`}
            laeuft={laeuft} zu={zu} breite={720}>
      <Fehlerkasten lage={fehler} />
      <div className="hinweis fehler">
        <b>Das ist kein Archivieren.</b> Die Zeilen verschwinden aus der
        Datenbank, mit Terminen, Aufmaßen, Dateibezügen und Behördenpapieren.
        Was im Protokoll bleibt, ist die Anschrift, jede Kennung und dieser
        Grund.
      </div>

      {!probe ? (
        <p className="klein leise">Es wird nachgesehen, was daran hängt.</p>
      ) : (
        <>
          {probe.mitArbeit > 0 && (
            <div className="hinweis warnung">
              An {probe.mitArbeit === 1 ? 'einem Auftrag' :
                  `${probe.mitArbeit} Aufträgen`} hängt Arbeit: Termine,
              Aufmaße, Dateien oder Behördenpapiere. Sie gehen mit.
            </div>
          )}
          <div className="rollt">
            <table className="liste">
              <thead>
                <tr>
                  <th>Auftrag</th>
                  <th style={{ width: 80 }}>Termine</th>
                  <th style={{ width: 90 }}>Aufmaß</th>
                  <th style={{ width: 80 }}>Dateien</th>
                  <th style={{ width: 90 }}>Papiere</th>
                </tr>
              </thead>
              <tbody>
                {probe.zeilen.map((z: any) => (
                  <tr key={z.id}>
                    <td>{z.fehlt
                      ? <span className="leise">gibt es nicht mehr</span>
                      : z.steckbrief}</td>
                    <td>{z.termine ?? ''}</td>
                    <td>{z.aufmass ?? ''}</td>
                    <td>{z.dateien ?? ''}</td>
                    <td>{z.papiere ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Text name="grund" titel="Warum?" pflicht mehrzeilig
            wert={grund} setze={setGrund} fehler={fehler}
            hinweis="Steht danach im Protokoll. Nachsehen lässt sich nichts
                     mehr - also muss dieser Satz die Antwort tragen." />

      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf gefahr"
                disabled={laeuft || !probe || grund.trim().length < 3}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    await api.auftraegeLoeschen(ids, grund);
                    await fertig();
                  } catch (f) { setFehler(ausFehler(f)); }
                  finally { setLaeuft(false); }
                }}>
          {laeuft ? 'Einen Moment'
                  : ids.length === 1 ? 'Endgültig löschen'
                                     : `${ids.length} endgültig löschen`}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


/**
 * Die drei Gewerkemarken mit ihrem Termin.
 *
 * Ein Gewerk, das nach dem Auftragstyp gar nicht unseres ist, wird blass
 * dargestellt und traegt es im Titel: bei einem reinen Montageauftrag stand
 * sonst ewig ein offener Tiefbau, den wir nie machen werden.
 */
function Gewerke({ gewerke, soll }: { gewerke: any[]; soll: any }) {
  const unser: Record<string, boolean> = {
    TB: soll?.tiefbau !== false,
    EB: soll?.einblasen !== false,
    SP: soll?.spleissen !== false,
  };
  const sichtbar = gewerke.filter((g) => g.art !== 'DK');
  if (!sichtbar.length) {
    return <span className="leise klein">noch keins vergeben</span>;
  }
  return (
    <div className="gewerkreihe">
      {sichtbar.map((g) => (
        <span
          key={g.art}
          className={
            'gewerk '
            + (unser[g.art] === false ? 'fremd'
               : g.erledigt ? 'fertig' : g.datum ? 'offen' : '')
          }
          title={
            unser[g.art] === false
              ? 'Nach dem Auftragstyp nicht unser Gewerk.'
              : (g.erledigt
                  ? `Erledigt${g.erledigt_am ? ' am ' + datumKurz(g.erledigt_am) : ''}. `
                  : '')
                + (g.firma ?? (g.vergeben ? 'andere Partnerfirma' : 'keine Firma'))
                + (g.datum ? `, ${datumKurz(g.datum)} ${uhr(g.von)}` : '')
          }
        >
          {g.art}
          {/* DAS DATUM STEHT AUCH DANN DA, WENN ES ERLEDIGT IST.
              Marvin, 31.08.2026: „das kann dann hier unter gewerke auch
              stehen, dass Tb abgeschlossen ist mit den notwendigen infos."
              Vorher zeigte die Marke bei einem erledigten Gewerk NUR das
              Kürzel - „TB", blass, ohne alles. Wann er fertig wurde, ist aber
              genau die Angabe, nach der der Monteur sucht: er kommt danach. */}
          {g.erledigt && (g.erledigt_am || g.datum) && (
            <span className="wann">
              ✓ {datumKurz(g.erledigt_am ?? g.datum)}
            </span>
          )}
          {g.datum && !g.erledigt && (
            <span className="wann">{datumKurz(g.datum)}</span>
          )}
        </span>
      ))}
    </div>
  );
}


/**
 * Die Rufnummern, anklickbar.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „bei aufträgen fehlen die Auftragsinfos und die rufnummern auf der
 *    habtseite."
 *
 * `webextel:` waehlt im Tischtelefon. DER KLICK DARF DIE ZEILE NICHT OEFFNEN:
 * wer die Nummer anklickt, will telefonieren und nicht die Akte aufmachen.
 * Deshalb haelt der Verweis das Ereignis an.
 */
function Rufnummern({ a }: { a: any }) {
  const nummern = [
    { text: a.kunde_telefon, art: 'Festnetz' },
    { text: a.kunde_mobil, art: 'Mobil' },
  ].filter((n) => waehlbar(n.text));
  if (!nummern.length) return null;
  return (
    <div className="rufnummern">
      {nummern.map((n) => (
        <a key={n.art} href={'webextel:' + waehlbar(n.text)}
           title={n.art + ' anrufen'}
           onClick={(e) => e.stopPropagation()}>
          {n.text}
        </a>
      ))}
    </div>
  );
}

/**
 * Der letzte Eintrag aus dem Verlauf.
 *
 * EINER, nicht drei. Der Server liefert die letzten drei, damit die Akte sie
 * nicht noch einmal holen muss; in der Liste steht der neueste, und die Zahl
 * daneben sagt, wie viele es insgesamt sind. Eine Liste, in der jede Zeile
 * drei Saetze traegt, liest niemand mehr.
 */
function Auftragsinfo({ a }: { a: any }) {
  const liste: any[] = Array.isArray(a.infos) ? a.infos : [];
  if (!liste.length) {
    return <span className="leise">nichts vermerkt</span>;
  }
  const letzte = liste[0];
  return (
    <div className="auftragsinfo" title={liste.map((x) => x.text).join('\n')}>
      <span>{letzte.text}</span>
      <div className="leise" style={{ fontSize: 11 }}>
        {datum(letzte.am)}
        {a.info_anzahl > 1 && `, ${a.info_anzahl} Einträge`}
      </div>
    </div>
  );
}
